import './ffsCalc/ffsCalcCardStyles.css'

import { LockOutlined } from '@ant-design/icons'
import { Alert, Select, Tabs, TabsProps, Tooltip, Typography } from 'antd'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoComponentModel } from 'types'

import { computeFfsEligibility } from './ffsCalc/ffsResultPublish/computeFfsEligibility'
import { computeFfsLevelAvailability } from './ffsCalc/ffsResultPublish/ffsLevelAvailability'
import { useSavedReports } from './ffsCalc/ffsResultPublish/ffsLtaPublishApi'
import { FfsRegionClassification } from './ffsCalc/ffsResultPublish/ffsRegionClassificationSchema'
import { FfsLevel, FfsPart } from './ffsCalc/ffsResultPublish/ffsTypes'
import { FfsSharedValueProvider } from './ffsCalc/FfsSharedValues'
import { FfsStrategyProvider } from './ffsCalc/ffsStrategyContext/FfsStrategyContext'
import { FfsPartFourCalc } from './ffsCalc/part4/FfsPartFourCalc'
import { FfsPartFiveLevelOneCalc } from './ffsCalc/part5LevelOne/FfsLevelOneCalc'
import { FfsPartFiveLevelTwoCalc } from './ffsCalc/part5LevelTwo/FfsLevelTwoCalc'

const { Title } = Typography

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const PartSelectorContainer = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
`

const LockedTabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`

interface FfsTabContentConfig {
  displayName: string
  levels: {
    key: string
    level: FfsLevel
    label: string
    component: FC<FfsCalcCardContentProps>
  }[]
}

const FFS_TAB_COMPONENT_REGISTRY: Record<FfsPart, FfsTabContentConfig> = {
  [FfsPart.PART_4]: {
    displayName: 'Part 4: General Corrosion',
    levels: [
      {
        key: 'ffs-part4-level-1',
        level: '1' as FfsLevel,
        label: 'Level 1',
        component: FfsPartFourCalc,
      },
      {
        key: 'ffs-part4-level-2',
        level: '2' as FfsLevel,
        label: 'Level 2',
        component: FfsPartFourCalc,
      },
    ],
  },
  [FfsPart.PART_5]: {
    displayName: 'Part 5: Local Metal Loss',
    levels: [
      {
        key: 'ffs-part5-level-1',
        level: '1' as FfsLevel,
        label: 'Level 1',
        component: FfsPartFiveLevelOneCalc,
      },
      {
        key: 'ffs-part5-level-2',
        level: '2' as FfsLevel,
        label: 'Level 2',
        component: FfsPartFiveLevelTwoCalc,
      },
    ],
  },
}

export interface FfsCalcCardContentProps {
  ffsLevel: FfsLevel
}

/**
 * Detail-card config for the FFS calc card.
 *
 * - When `ffsPart` is set (Libra part-scoped steps), the card pins that part
 *   (hiding the Part selector) and gates the Level tabs: for a CLASSIFIED LTA,
 *   Level 2 stays disabled until this LTA has a saved Level 1 report, and Level 1
 *   is disabled (Level 2 auto-selected) when `computeFfsEligibility` reports it
 *   ineligible. An UNCLASSIFIED LTA unlocks both levels immediately
 *   (`skipLevel1Gate`).
 * - When `ffsPart` is absent (FDE details page), the card behaves exactly as
 *   before: a Part dropdown and always-enabled Level tabs.
 *
 * `classification` is the selected LTA's committed region classification,
 * injected via the config channel by `normalizeFfsDetailCardsForPart` (detail
 * cards can't take arbitrary props). It drives which levels are eligible. A
 * MISSING or unparseable classification is treated PERMISSIVELY (both levels
 * runnable, no L1-first ordering) so FFS Pre-Planning can be skipped -- the card
 * surfaces a non-blocking warning instead. Every type-eligible (part, level) is
 * runnable in ANY order -- there is no "Part 4 first" precondition; availability
 * is a pure function of the type matrix. A type that cannot be assessed in the
 * pinned part at all (Type C anywhere; B2 under Part 5) still locks it.
 */
export interface FfsCalcCardConfig {
  ffsPart?: FfsPart
  classification?: FfsRegionClassification | null
}

export const Width = '24rem'

export const CardView: FC<IndicationDetailCardProps<FfsCalcCardConfig>> = ({
  config,
}) => {
  const {
    assetModel,
    relevantThicknessCDF: thicknessCdf,
    inspection,
  } = useIndicationData()

  const pinnedPart = config?.ffsPart
  const [selectedPartState, setSelectedPart] = useState<FfsPart>(FfsPart.PART_4)
  const selectedPart = pinnedPart ?? selectedPartState
  const isPinned = pinnedPart !== undefined

  const componentModel: GeckoComponentModel | undefined = useMemo(() => {
    const components = assetModel?.data.components
    const componentId = thicknessCdf?.tags.component
    if (!components || !componentId) {
      return undefined
    }

    return components.get(componentId) ?? undefined
  }, [assetModel?.data.components, thicknessCdf?.tags.component])

  const ffsCalcCardContentProps = useMemo(
    () => ({
      componentModel,
    }),
    [componentModel]
  )

  // Per-LTA level gating (only applied when the card is pinned to one part).
  // `useSavedReports` is already scoped to the current indication, so
  // `level1Done` is per-LTA for free. While reports load, `level1Done` is false
  // -- keeping Level 2 locked -- so the tab never flickers locked->unlocked.
  // Only the pinned (Libra) path reads reports; skip the query on the unpinned
  // FDE path.
  const { reports } = useSavedReports(isPinned)
  // Every type-eligible (part, level) is runnable in any order — no "Part 4
  // first" precondition. Availability is a pure function of the type matrix.
  const eligibility = useMemo(
    () =>
      computeFfsEligibility({
        part: selectedPart,
        classification: config?.classification,
      }),
    [selectedPart, config?.classification]
  )
  const level1Done = useMemo(
    () =>
      reports.some(
        (report) => report.ffs_part === selectedPart && report.ffs_level === '1'
      ),
    [reports, selectedPart]
  )

  const levelAvailability = useMemo(
    () => computeFfsLevelAvailability({ isPinned, eligibility, level1Done }),
    [isPinned, eligibility, level1Done]
  )
  const levelState = useMemo(
    () =>
      FFS_TAB_COMPONENT_REGISTRY[selectedPart].levels.map((levelConfig) => ({
        ...levelConfig,
        ...levelAvailability[levelConfig.level],
      })),
    [selectedPart, levelAvailability]
  )

  // Controlled active key with coercion (pinned only). Keep the user's choice
  // while it points at an enabled tab; otherwise fall to the first enabled tab.
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined)
  const enabledKeys = useMemo(
    () =>
      levelState.filter((level) => !level.disabled).map((level) => level.key),
    [levelState]
  )
  const effectiveActiveKey =
    activeKey && enabledKeys.includes(activeKey) ? activeKey : enabledKeys[0]

  const tabItems = useMemo<TabsProps['items']>(() => {
    return levelState.map((levelConfig) => {
      const CalcComponent: FC<FfsCalcCardContentProps> = levelConfig.component
      const label =
        levelConfig.disabled && levelConfig.lockReason ? (
          <Tooltip title={levelConfig.lockReason}>
            <LockedTabLabel>
              <LockOutlined />
              {levelConfig.label}
            </LockedTabLabel>
          </Tooltip>
        ) : (
          levelConfig.label
        )
      return {
        key: levelConfig.key,
        label,
        disabled: levelConfig.disabled,
        children: (
          <CalcComponent
            {...ffsCalcCardContentProps}
            ffsLevel={levelConfig.level}
          />
        ),
      }
    })
  }, [ffsCalcCardContentProps, levelState])

  const noLevelsAvailable = isPinned && enabledKeys.length === 0

  return (
    <CardContainer>
      <Title level={3}>FFS Calculations</Title>
      {!isPinned ? (
        <PartSelectorContainer>
          <Select
            value={selectedPart}
            onChange={setSelectedPart}
            style={{ width: '100%' }}
            options={Object.entries(FFS_TAB_COMPONENT_REGISTRY).map(
              ([part, partConfig]) => ({
                value: part as FfsPart,
                label: partConfig.displayName,
              })
            )}
          />
        </PartSelectorContainer>
      ) : null}
      <FfsStrategyProvider
        componentModelType={componentModel?.type}
        ffsPart={selectedPart}>
        <FfsSharedValueProvider
          componentModel={componentModel}
          thicknessCdf={thicknessCdf}
          inspection={inspection}>
          {isPinned && !config?.classification ? (
            <Alert
              type='warning'
              showIcon
              style={{ marginBottom: '0.75rem' }}
              message="FFS Pre-Planning hasn't been completed for this LTA, so its FFS route and starting level (Level 1 vs Level 2) haven't been confirmed. You can still run the assessment — it defaults to a Level 1 entry."
            />
          ) : null}
          {noLevelsAvailable ? (
            <Alert
              type='info'
              showIcon
              message={
                eligibility.reasons?.level1 ??
                eligibility.reasons?.level2 ??
                'No FFS levels are applicable for this component.'
              }
            />
          ) : (
            <Tabs
              activeKey={effectiveActiveKey}
              onChange={setActiveKey}
              items={tabItems}
              animated={{ inkBar: true, tabPane: true }}
            />
          )}
        </FfsSharedValueProvider>
      </FfsStrategyProvider>
    </CardContainer>
  )
}
